import { useEffect, useRef } from 'react';
import type { AppNotification } from './api';
import type { Translate } from './i18n';

/** The top-right notification bell and its dropdown. */
export function NotificationsMenu({
  notifications,
  unread,
  open,
  onToggle,
  onClose,
  onMarkRead,
  onRead,
  t,
}: {
  notifications: AppNotification[];
  unread: number;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onMarkRead: () => void;
  onRead: (id: string) => void;
  t: Translate;
}) {
  const wrap = useRef<HTMLDivElement>(null);

  // Click outside or press Escape to close the popover.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  return (
    <div className="notif-wrap" ref={wrap}>
      <button className={open ? 'active' : ''} onClick={onToggle} aria-expanded={open}>
        🔔{unread > 0 ? ` ${unread}` : ''}
      </button>
      {open && (
        <div className="notif-popover">
          <div className="panel-head">
            <strong>{t('notifications.title')}</strong>
            <button className="link" onClick={onClose} aria-label={t('actions.dismiss')}>
              ✕
            </button>
          </div>
          <div className="toolbar">
            <button disabled={unread === 0} onClick={onMarkRead}>
              {t('notifications.markRead')}
            </button>
          </div>
          {notifications.length === 0 && <p>{t('notifications.empty')}</p>}
          <ul>
            {notifications.map((n) => (
              <li key={n.id} className={n.readAt ? 'read' : ''}>
                <span className="change-name">{n.title ?? n.kind}</span>
                <em>{new Date(n.createdAt).toLocaleString()}</em>
                {!n.readAt && (
                  <button
                    className="link"
                    title={t('notifications.markReadOne')}
                    aria-label={t('notifications.markReadOne')}
                    onClick={() => onRead(n.id)}
                  >
                    ✓
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
